import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

// Disable native browser scroll restoration. This component handles it instead.
if (typeof window !== 'undefined') {
  window.history.scrollRestoration = 'manual';
}

// Pages with content-aware scroll restoration (e.g. Market) set this flag
// in sessionStorage before navigating away to skip the generic Y restore.
// The page clears this flag itself once it has finished its own restore.
export const SKIP_SCROLL_RESTORE_KEY = '__skipGenericScrollRestore__';

const STORAGE_KEY = (key) => `__scroll__${key}`;
const RESTORE_DELAYS_MS = [0, 50, 150, 300, 600];

// Track scroll position continuously so save() is never racing with a layout change.
// useEffect fires after paint; by then a tab switch may have already changed the DOM
// height, so reading window.scrollY inside the effect can give the wrong value.
let _lastScrollY = typeof window !== 'undefined' ? window.scrollY : 0;
if (typeof window !== 'undefined') {
  window.addEventListener('scroll', () => { _lastScrollY = window.scrollY; }, { passive: true });
}

function getLocationKey(location) {
  return `${location.pathname}${location.hash}`;
}

function save(locationKey) {
  sessionStorage.setItem(STORAGE_KEY(locationKey), String(_lastScrollY));
}

function restore(locationKey) {
  const raw = sessionStorage.getItem(STORAGE_KEY(locationKey));
  const y = raw !== null ? parseInt(raw, 10) : 0;

  // The destination route may render async content first (e.g. a spinner, then
  // the supplements directory). A single early scroll can be clamped to 0 if the
  // document is not tall enough yet, so retry briefly as content settles.
  RESTORE_DELAYS_MS.forEach((delay) => {
    setTimeout(() => {
      window.scrollTo({ top: y, behavior: 'auto' });
    }, delay);
  });
}

function scrollToHash(hash) {
  const id = decodeURIComponent(hash.replace(/^#/, ''));
  if (!id) return;

  RESTORE_DELAYS_MS.forEach((delay) => {
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ block: 'start', behavior: 'auto' });
      }
    }, delay);
  });
}

/**
 * Mount once inside <Router>, above all <Routes>.
 *
 * Rules:
 *  - POP navigation restores saved Y for that route
 *  - PUSH / REPLACE navigation scrolls to top
 */
export default function ScrollRestoration() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const currentLocationKey = getLocationKey(location);
  const locationKeyRef = useRef(currentLocationKey);

  // Save before route changes caused by normal in-page clicks. This runs in the
  // capture phase, before React Router updates location and before the outgoing
  // route can shrink the document and clamp scrollY to 0.
  useEffect(() => {
    const saveCurrentPosition = () => save(locationKeyRef.current);
    window.addEventListener('click', saveCurrentPosition, true);
    return () => window.removeEventListener('click', saveCurrentPosition, true);
  }, []);

  useEffect(() => {
    locationKeyRef.current = currentLocationKey;

    if (navigationType === 'POP') {
      const skip = sessionStorage.getItem(SKIP_SCROLL_RESTORE_KEY);
      if (skip) {
        sessionStorage.removeItem(SKIP_SCROLL_RESTORE_KEY);
      } else {
        restore(currentLocationKey);
      }
    } else if (location.hash) {
      scrollToHash(location.hash);
    } else {
      // PUSH or REPLACE: always start at top.
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [currentLocationKey, navigationType]);

  // Save on unmount / tab close
  useEffect(() => {
    const onUnload = () => save(locationKeyRef.current);
    window.addEventListener('beforeunload', onUnload);
    return () => {
      save(locationKeyRef.current);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, []);

  return null;
}
